import React, { useMemo } from 'react';
import { formatCurrency } from '../../../Help/helper';
import { computeMonthlySummary } from '../utils/reportAggregations';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const SummaryTab = ({ data }) => {
  const { monthInvoices, monthStart } = data;

  const summary = useMemo(
    () => computeMonthlySummary(monthInvoices),
    [monthInvoices]
  );

  const monthLabel = `${MONTH_NAMES[monthStart.getMonth()]} ${monthStart.getFullYear()}`;

  const marginPercent = summary.totalSpareSale > 0
    ? ((summary.sparePartMargin / summary.totalSpareSale) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-6">
      {/* Month header */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3">
          <h3 className="text-lg font-bold text-white">Monthly Summary — {monthLabel}</h3>
          <p className="text-indigo-200 text-sm">Income, spare part margin, labour, and gross profit</p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Income */}
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
          <p className="text-emerald-100 text-sm font-medium">Total Income</p>
          <h3 className="text-2xl font-bold mt-2">{formatCurrency(summary.totalIncome)}</h3>
          <p className="text-emerald-100 text-xs mt-2">{summary.invoiceCount} invoices</p>
        </div>

        {/* Spare Part Margin */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
          <p className="text-blue-100 text-sm font-medium">Spare Part Margin</p>
          <h3 className="text-2xl font-bold mt-2">{formatCurrency(summary.sparePartMargin)}</h3>
          <p className="text-blue-100 text-xs mt-2">{marginPercent}% margin on parts</p>
        </div>

        {/* Labour */}
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
          <p className="text-amber-100 text-sm font-medium">Labour Revenue</p>
          <h3 className="text-2xl font-bold mt-2">{formatCurrency(summary.totalLabour)}</h3>
          <p className="text-amber-100 text-xs mt-2">Service &amp; labour charges</p>
        </div>

        {/* Gross Profit */}
        <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
          <p className="text-purple-100 text-sm font-medium">Gross Profit</p>
          <h3 className="text-2xl font-bold mt-2">{formatCurrency(summary.grossProfit)}</h3>
          <p className="text-purple-100 text-xs mt-2">Margin + Labour</p>
        </div>
      </div>

      {/* Detailed breakdown table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-3">
          <h3 className="text-lg font-bold text-white">Detailed Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900">Total Income (Invoice Value)</td>
                <td className="px-6 py-4 text-sm text-right font-medium text-green-600">{formatCurrency(summary.totalIncome)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900">Spare Parts Sale</td>
                <td className="px-6 py-4 text-sm text-right text-gray-700">{formatCurrency(summary.totalSpareSale)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900">Spare Parts Cost</td>
                <td className="px-6 py-4 text-sm text-right text-red-600">{formatCurrency(summary.totalSpareCost)}</td>
              </tr>
              <tr className="bg-blue-50">
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">Spare Part Margin (Sale - Cost)</td>
                <td className="px-6 py-4 text-sm text-right font-bold text-blue-700">{formatCurrency(summary.sparePartMargin)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900">Labour Revenue</td>
                <td className="px-6 py-4 text-sm text-right font-medium text-amber-600">{formatCurrency(summary.totalLabour)}</td>
              </tr>
              <tr className="bg-purple-50">
                <td className="px-6 py-4 text-sm font-bold text-gray-900">Gross Profit (Margin + Labour)</td>
                <td className="px-6 py-4 text-sm text-right font-bold text-purple-700">{formatCurrency(summary.grossProfit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
